import { Box } from "@mui/material";
import {
  CategoryNav,
  type CategoryNavItem,
} from "components/navigation/CategoryNav";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface DiscoveryNavConfig {
  items: CategoryNavItem[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
}

interface DiscoveryNavRegisterContextValue {
  register: (config: DiscoveryNavConfig | null) => void;
  scrollIntoView: () => void;
}

// Split from the config context on purpose: this value's identity never
// changes (register/scrollIntoView are stable callbacks), so components that
// only call useRegisterDiscoveryNav — StartPage, SearchPage, DiscoverySection
// — never re-render just because *some* page's registered config changed.
// Bundling config into this same context caused exactly that: a registrant
// is also a context consumer, so a config change re-rendered it, which could
// recreate unmemoized registration args (items/onSelect), re-fire the
// registration effect, change config again, and loop.
const DiscoveryNavRegisterContext =
  createContext<DiscoveryNavRegisterContextValue | null>(null);

interface DiscoveryNavConfigContextValue {
  config: DiscoveryNavConfig | null;
  navRef: React.RefObject<HTMLElement | null>;
}

const DiscoveryNavConfigContext =
  createContext<DiscoveryNavConfigContextValue | null>(null);

/**
 * Holds the discovery tab bar's config so it survives navigation between
 * sibling discovery pages (CX-3737) instead of unmounting with the page that
 * used to own it — a page publishes what the bar should show via
 * {@link useRegisterDiscoveryNav}. Rendering the bar itself is each page's
 * job via {@link DiscoveryNavOutlet}, placed wherever that page wants the
 * bar to appear (e.g. StartPage puts it below its banner carousel).
 */
export const DiscoveryNavProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<DiscoveryNavConfig | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const register = useCallback((next: DiscoveryNavConfig | null) => {
    setConfig(next);
  }, []);

  const scrollIntoView = useCallback(() => {
    setTimeout(() => {
      navRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const registerValue = useMemo(
    () => ({ register, scrollIntoView }),
    [register, scrollIntoView],
  );
  const configValue = useMemo(() => ({ config, navRef }), [config]);

  return (
    <DiscoveryNavRegisterContext.Provider value={registerValue}>
      <DiscoveryNavConfigContext.Provider value={configValue}>
        {children}
      </DiscoveryNavConfigContext.Provider>
    </DiscoveryNavRegisterContext.Provider>
  );
};

/**
 * Renders the currently registered discovery tab bar, or nothing if no page
 * has registered one. Each discovery page places this where it wants the
 * bar to appear rather than getting it forced above its own content.
 */
export const DiscoveryNavOutlet = () => {
  const context = useContext(DiscoveryNavConfigContext);
  if (!context?.config) {
    return null;
  }
  const { config, navRef } = context;
  return (
    <Box ref={navRef} component="div">
      <CategoryNav
        items={config.items}
        activeId={config.activeId}
        onSelect={config.onSelect}
      />
    </Box>
  );
};

const noop = () => {};

/**
 * Publishes the calling page's discovery nav config to the nearest
 * {@link DiscoveryNavProvider}. Unregisters on unmount so a page swap (e.g.
 * StartPage → SearchPage) hands the bar to whichever page mounts next.
 * A no-op outside a provider, mirroring useRegisterNavigationGuard.
 */
export const useRegisterDiscoveryNav = ({
  items,
  activeId,
  onSelect,
}: DiscoveryNavConfig) => {
  const context = useContext(DiscoveryNavRegisterContext);
  const register = context?.register;
  useEffect(() => {
    register?.({ items, activeId, onSelect });
    return () => register?.(null);
  }, [register, items, activeId, onSelect]);
  return { scrollIntoView: context?.scrollIntoView ?? noop };
};
